import { useEffect, useMemo, useState } from "react";
import { adminListLoyaltyMembers } from "@/lib/api";
import { ClubImportCsvButton, ClubVoucherSettings } from "@/components/ClubImportPanel";

/**
 * Naami Club member browser for the admin dashboard.
 * Shows every member with their live points balance, tier, lifetime spend,
 * order count and referral code. Searchable + sortable. Master-admin only.
 *
 * Rendered as a full-screen modal so operators can scan through hundreds of
 * members without the parent brand list scrolling behind them.
 */
export const LoyaltyMembersModal = ({ onClose }) => {
    const [members, setMembers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("points_desc");

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const r = await adminListLoyaltyMembers({ search: search || undefined, sort, limit: 1000 });
            setMembers(r.members || []);
            setSummary(r.summary || null);
        } catch (ex) {
            setErr(ex.response?.data?.detail || "Failed to load members");
        } finally {
            setLoading(false);
        }
    };

    // Re-load whenever the sort changes. Search is applied on debounce (below).
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort]);

    // Debounce search — 400ms after typing stops
    useEffect(() => {
        const t = setTimeout(load, 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const tierColor = (tier) => (
        { Bronze: "#cd7f32", Silver: "#c0c0c0", Gold: "#e6b800" }[tier] || "#888"
    );

    // Show the summary from the FULL member set, not the filtered rows —
    // gives the operator the true business KPIs regardless of filter.
    const kpi = useMemo(() => summary || {}, [summary]);

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div
                className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-5xl my-8 p-6 md:p-8"
                onClick={(e) => e.stopPropagation()}
                data-testid="loyalty-members-modal"
            >
                {/* Header */}
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <p className="eyebrow mb-1">// Naami Club</p>
                        <h2 className="font-display text-[32px] leading-[0.95] text-gold">Members.</h2>
                    </div>
                    <button onClick={onClose} className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-gold" data-testid="loyalty-close">
                        ✕ Close
                    </button>
                </div>

                {/* KPI cards — total members, points outstanding, spend, orders */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <Kpi label="Members" value={kpi.total_members ?? "—"} testid="kpi-members" />
                    <Kpi label="Points out" value={(kpi.total_points_outstanding ?? 0).toLocaleString()} testid="kpi-points" />
                    <Kpi label="Lifetime spend" value={`£${(kpi.total_lifetime_spend ?? 0).toFixed(2)}`} highlight testid="kpi-spend" />
                    <Kpi label="Orders" value={(kpi.total_orders ?? 0).toLocaleString()} testid="kpi-orders" />
                </div>

                <div className="grid grid-cols-2 gap-3 mb-5">
                    <Kpi label="Club voucher members" value={kpi.club_voucher_members ?? 0} testid="kpi-club-members" />
                    <Kpi label="Vouchers redeemed" value={kpi.club_vouchers_redeemed ?? 0} testid="kpi-club-redeemed" />
                </div>
                <div className="mb-4"><ClubImportCsvButton onDone={load} /></div>
                <ClubVoucherSettings />

                {/* Search + sort */}
                <div className="flex flex-wrap gap-2 mb-4">
                    <input
                        className="input flex-1 min-w-[220px]"
                        type="search"
                        placeholder="Search by name / phone / email / referral or voucher code"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="loyalty-search"
                    />
                    <select
                        className="input !w-52"
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        data-testid="loyalty-sort"
                    >
                        <option value="points_desc">Most points</option>
                        <option value="spend_desc">Highest spend</option>
                        <option value="orders_desc">Most orders</option>
                        <option value="recent">Recently joined</option>
                        <option value="name">Name (A→Z)</option>
                    </select>
                </div>

                {loading && <p className="text-muted mt-4">Loading members…</p>}
                {err && <p className="text-[#ff5246] text-sm font-mono mt-4">{err}</p>}

                {!loading && !err && (
                    <div className="border border-[var(--border)] divide-y divide-[var(--border)]" data-testid="loyalty-members-list">
                        {/* Column header */}
                        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[var(--surface-elevated)] font-mono text-[9px] uppercase tracking-[0.18em] text-muted">
                            <span className="col-span-4 md:col-span-3">Name / contact</span>
                            <span className="col-span-1 hidden md:block">Tier</span>
                            <span className="col-span-2 md:col-span-1 text-right">Points</span>
                            <span className="col-span-1 text-right hidden md:block">Spend</span>
                            <span className="col-span-1 text-right hidden md:block">Orders</span>
                            <span className="col-span-1 text-right hidden md:block">Ref</span>
                            <span className="col-span-4 md:col-span-3 text-right">Voucher</span>
                            <span className="col-span-2 md:col-span-1 text-right">Redeemed</span>
                        </div>
                        {members.length === 0 ? (
                            <div className="p-6 text-center text-muted">
                                {search ? `No members match "${search}"` : "No members yet."}
                            </div>
                        ) : (
                            members.map((m) => (
                                <div
                                    key={m.id}
                                    className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center text-[13px]"
                                    data-testid={`loyalty-row-${m.id}`}
                                >
                                    <div className="col-span-4 md:col-span-3 min-w-0">
                                        <div className="truncate">{m.name || "—"}</div>
                                        <div className="font-mono text-[10px] text-muted truncate">
                                            {m.phone || "no phone"}
                                        </div>
                                        <div className="font-mono text-[10px] text-muted truncate">
                                            {m.email || "no email"}
                                        </div>
                                    </div>
                                    <div className="col-span-1 hidden md:block">
                                        <span
                                            className="inline-block font-mono text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border"
                                            style={{ color: tierColor(m.tier), borderColor: tierColor(m.tier) }}
                                        >
                                            {m.tier || "Bronze"}
                                        </span>
                                    </div>
                                    <div className="col-span-2 md:col-span-1 text-right font-mono text-gold">
                                        {(m.points || 0).toLocaleString()}
                                    </div>
                                    <div className="col-span-1 text-right font-mono text-muted hidden md:block">
                                        £{(m.lifetime_spend || 0).toFixed(2)}
                                    </div>
                                    <div className="col-span-1 text-right font-mono text-muted hidden md:block">
                                        {m.orders_count || 0}
                                    </div>
                                    <div className="col-span-1 text-right font-mono text-[10px] text-muted truncate hidden md:block">
                                        {m.referral_code || "—"}
                                    </div>
                                    <div className="col-span-4 md:col-span-3 text-right font-mono text-[11px] truncate" data-testid={`loyalty-voucher-${m.id}`}>
                                        {m.voucher_code || "—"}
                                        {m.bonus_codes?.length > 0 && (
                                            <div className="text-[9px] text-muted truncate">+{m.bonus_codes.join(" ")}</div>
                                        )}
                                    </div>
                                    <div className={`col-span-2 md:col-span-1 text-right font-mono text-[10px] uppercase ${m.voucher_used ? "text-[#ff5246]" : "text-muted"}`} data-testid={`loyalty-redeemed-${m.id}`}>
                                        {m.voucher_code ? (m.voucher_used ? "Yes" : "No") : "—"}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-4">
                    Points: 1 pt per £1 spent · 100 pts = £5 off · Silver 1.25× · Gold 1.5×
                </p>
            </div>
        </div>
    );
};

const Kpi = ({ label, value, highlight, testid }) => (
    <div
        data-testid={testid}
        className={`bg-[var(--surface-elevated)] border ${highlight ? "border-gold" : "border-[var(--border)]"} p-3`}
    >
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-muted mb-1">{label}</div>
        <div className={`font-display text-[22px] leading-none ${highlight ? "text-gold" : ""}`}>{value}</div>
    </div>
);

export default LoyaltyMembersModal;
